import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {BaseComponent, IntervalValidatorContent, TemplateFeature} from 'compob2b-libs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {CustomValidators} from '../../../../../../../admin-shared/custom-validators/CustomValidators';
import {DamItemTemplateFeaturesService, TemplateFeatureValue} from '../../dam-item-template-features.service';

@Component({
  selector: 'app-tf-interval',
  templateUrl: './tf-interval.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TfIntervalComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;

  featureValuePlaceholder: string;

  form = new UntypedFormGroup({
    featureValue: new UntypedFormControl(null)
  });

  validatorName: string = null;
  intervalValidator: IntervalValidatorContent = null;
  valueExample: string = null;

  constructor(protected readonly templateFeatureService: DamItemTemplateFeaturesService) {
    super();
  }

  get featureValue(): any {
    return this.form.get('featureValue');
  }

  ngOnInit(): void {
    this.setValidator();

    this.form.get('featureValue').valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(data => {
        this.setFeature(data !== null && data.trim() !== '' ? data.trim() : null, this.form.valid, this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  isEmpty(): boolean {
    return !this.featureValue.value;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  private setValidator(): void {
    if (this.feature.validator !== null) {
      this.validatorName = this.feature.validator.header;
      this.intervalValidator = this.feature.validator.content.intervalValidator;
      this.valueExample = this.feature.validator.example;

      this.featureValue.addValidators(CustomValidators.intervalValueValidator(this.intervalValidator));
    } else {
      this.featureValue.addValidators(
        Validators.pattern(this.feature.isInteger ? CustomValidators.INT_INTERVAL_PATTERN : CustomValidators.FLOAT_INTERVAL_PATTERN)
      );
    }
  }

  private setDefaultValue(): void {
    this.featureValuePlaceholder = this.feature.isInteger ? '3...102' : '3...102.3';

    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel) {
      this.featureValue.setValue(featureModel[this.feature.id], {emitEvent: false});
    }

    this.setDefaultFeatureValue(this.featureValue.value);
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      this.form.valid,
      this.isShowEmptyWarning()
    );
  }
}
